import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import AssetOwnerImage from "../AssetOwnerImage";

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function FieldLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "error.main" }}>
        *
      </Box>
    </Typography>
  );
}

function sanitizeNumeric(v) {
  const s = v.replace(/[^\d.]/g, "");
  const i = s.indexOf(".");
  return i === -1 ? s : s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
}

function sanitizeInteger(v) {
  return v.replace(/\D/g, "");
}

const initialForm = {
  assetId: "",
  poolName: "",
  annualYield: "",
  lockPeriod: "",
  minimumStake: "",
  targetStakeAmount: "",
};

export default function ListStakingPoolModal({ open, onClose, assetOptions = [], onSubmit }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm);
    setErrors({});
    setSubmitting(false);
  }, [open]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const requestClose = () => {
    if (!submitting) onClose();
  };

  const validate = () => {
    const next = {};
    if (!form.assetId) next.assetId = "Please select an asset.";
    if (!form.poolName.trim()) next.poolName = "Pool name is required.";
    const yieldNum = parseFloat(form.annualYield);
    if (!form.annualYield || !Number.isFinite(yieldNum) || yieldNum <= 0) {
      next.annualYield = "Enter a valid annual yield.";
    } else if (yieldNum > 100) {
      next.annualYield = "Annual yield must be 100% or less.";
    }
    const lockNum = parseInt(form.lockPeriod, 10);
    if (!form.lockPeriod || !Number.isFinite(lockNum) || lockNum <= 0) {
      next.lockPeriod = "Enter a valid lock period.";
    }
    const minStakeNum = parseInt(form.minimumStake, 10);
    if (!form.minimumStake || !Number.isFinite(minStakeNum) || minStakeNum <= 0) {
      next.minimumStake = "Enter a valid minimum stake.";
    }
    const targetStakeNum = parseInt(form.targetStakeAmount, 10);
    if (!form.targetStakeAmount || !Number.isFinite(targetStakeNum) || targetStakeNum <= 0) {
      next.targetStakeAmount = "Enter a valid target stake amount.";
    } else if (!next.minimumStake && targetStakeNum <= minStakeNum) {
      next.targetStakeAmount = "Target stake amount must be more than the minimum stake.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const asset = assetOptions.find((a) => a.id === form.assetId);
    setSubmitting(true);
    try {
      await onSubmit?.({
        assetId: form.assetId,
        assetName: asset?.name || "",
        poolName: form.poolName.trim(),
        annualYield: parseFloat(form.annualYield),
        lockPeriod: parseInt(form.lockPeriod, 10),
        minStake: parseInt(form.minimumStake, 10),
        targetStakeAmount: parseInt(form.targetStakeAmount, 10),
      });
      onClose();
    } catch {
      /* API error is already surfaced via the global toast; keep the modal open */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <SavingsRoundedIcon sx={{ color: "text.slate", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={20} sx={{ color: "text.primary" }}>
                List Staking Pool
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Set up a new staking pool for one of your tokenized assets.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={requestClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <FieldLabel>Select Asset</FieldLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={form.assetId}
              onChange={(e) => setField("assetId", e.target.value)}
              error={!!errors.assetId}
              displayEmpty
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  const selected = assetOptions.find((a) => a.id === value);
                  if (!selected) {
                    return (
                      <Typography component="span" sx={{ color: "text.faint" }}>
                        Select an asset
                      </Typography>
                    );
                  }
                  return (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AssetOwnerImage
                        src={selected.imageUrl}
                        alt=""
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 4,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                      <span>{selected.name}</span>
                    </Stack>
                  );
                },
              }}
              sx={{ mt: 1 }}
            >
              {assetOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  <em>No active assets available</em>
                </MenuItem>
              ) : (
                assetOptions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <AssetOwnerImage
                        src={a.imageUrl}
                        alt=""
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 4,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                      <span>{a.name}</span>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </TextField>
            {errors.assetId ? (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.assetId}</FormHelperText>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Only your active assets are eligible for staking pools.
              </Typography>
            )}
          </Box>

          <Box>
            <FieldLabel>Pool Name</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g., Downtown Tower Staking Pool"
              value={form.poolName}
              onChange={(e) => setField("poolName", e.target.value)}
              error={!!errors.poolName}
              sx={{ mt: 1 }}
            />
            {errors.poolName && <FormHelperText error sx={{ mt: 0.5 }}>{errors.poolName}</FormHelperText>}
          </Box>

          <Box>
            <FieldLabel>Annual Yield</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="0.0"
              value={form.annualYield}
              onChange={(e) => setField("annualYield", sanitizeNumeric(e.target.value))}
              error={!!errors.annualYield}
              inputProps={{ inputMode: "decimal" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ mt: 1 }}
            />
            {errors.annualYield && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.annualYield}</FormHelperText>
            )}
          </Box>

          <Box>
            <FieldLabel>Lock Period</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="90"
              value={form.lockPeriod}
              onChange={(e) => setField("lockPeriod", sanitizeInteger(e.target.value))}
              error={!!errors.lockPeriod}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{
                endAdornment: <InputAdornment position="end">days</InputAdornment>,
              }}
              sx={{ mt: 1 }}
            />
            {errors.lockPeriod && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.lockPeriod}</FormHelperText>
            )}
          </Box>

          <Box>
            <FieldLabel>Minimum Stake</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="0"
              value={form.minimumStake}
              onChange={(e) => setField("minimumStake", sanitizeInteger(e.target.value))}
              error={!!errors.minimumStake}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{
                startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
              }}
              sx={{ mt: 1 }}
            />
            {errors.minimumStake && (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.minimumStake}</FormHelperText>
            )}
          </Box>

          <Box>
            <FieldLabel>Target Stake Amount</FieldLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="0"
              value={form.targetStakeAmount}
              onChange={(e) => setField("targetStakeAmount", sanitizeInteger(e.target.value))}
              error={!!errors.targetStakeAmount}
              inputProps={{ inputMode: "numeric" }}
              InputProps={{
                startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
              }}
              sx={{ mt: 1 }}
            />
            {errors.targetStakeAmount ? (
              <FormHelperText error sx={{ mt: 0.5 }}>{errors.targetStakeAmount}</FormHelperText>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Must be greater than the minimum stake.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={requestClose}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            boxShadow: (theme) => theme.palette.brand.ctaShadow,
            background: (theme) => theme.palette.brand.ctaGradient,
            "&:hover": {
              boxShadow: (theme) => theme.palette.brand.ctaShadowHover,
              background: (theme) => theme.palette.brand.ctaGradientHover,
            },
          }}
        >
          {submitting ? "Creating…" : "Create Pool"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
